// web-frontend/src/components/common/NotificationModal.jsx
// 🔥🔥🔥 [2025-12-31 수정] 설문 알림 클릭 시 /survey/{slug}로 직접 이동 처리
// 🔥🔥🔥 [2025-12-31 수정] action_url의 /surveys/ → /survey/ 경로 보정
import React, { useState } from 'react';
import { X, Check, Clock, Trash2, Bell, Megaphone, AlertCircle } from 'lucide-react';
import useNotificationsStore from '../../shared/stores/notificationsStore';
import useAppStore from '../../shared/stores/appStore';
import i18nService from '../../shared/services/i18n';
import { useNavigate } from 'react-router-dom';
import './NotificationModal.css';

// =============================================================================
// 🔥🔥🔥 [2025-12-31 신규] 설문 관련 알림 타입 상수
// =============================================================================
const SURVEY_NOTIFICATION_TYPES = [
  'survey_reminder', 
  'survey_request', 
  'survey_scheduled',
  'satisfaction_survey'
];

const NotificationModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { notifications, markAsRead, markAllAsRead, deleteNotification } = useNotificationsStore();
  const { language } = useAppStore();
  // 스와이프 상태 관리
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipedId, setSwipedId] = useState(null);
  if (!isOpen) return null;

  const tr = (key, fallback, params = {}) => {
    const value = i18nService.t(key, params);
    return value && value !== key ? value : fallback;
  };

  const localeMap = {
    ko: 'ko-KR',
    en: 'en-US',
    ja: 'ja-JP',
    zh: 'zh-CN',
  };

  const formatAbsoluteDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleString(localeMap[language] || 'en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = (now - date) / 1000;
    
    if (diff < 60) return tr('notifications.time_just_now', 'Just now');
    if (diff < 3600) {
      return tr('notifications.time_minutes_ago', '{count}m ago', {
        count: Math.floor(diff / 60),
      });
    }
    if (diff < 86400) {
      return tr('notifications.time_hours_ago', '{count}h ago', {
        count: Math.floor(diff / 3600),
      });
    }
    return date.toLocaleDateString(localeMap[language] || 'en-US');
  };

  const getNotificationText = (noti) => {
    const data = noti.data || noti.metadata || noti.extra_data || {};
    const asText = (value) => {
      if (!value) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'object') {
        return value.details || value.room_name || value.name || '';
      }
      return String(value);
    };

    const rawTitle = noti.title || '';
    const rawMessage = noti.message || '';
    const defaultMeetingTitle = tr('notifications.meeting_default_title', 'Meeting');
    const inferLegacyMeetingContext = () => {
      const rawText = `${rawTitle}\n${rawMessage}`;
      const explicitMeetingEvent = data.event_key || data.eventKey || noti.event_key || '';
      const isMeetingNotification =
        noti.notification_type === 'meeting_invite' ||
        String(explicitMeetingEvent).startsWith('meeting_');

      if (!isMeetingNotification) return {};

      const actorFromTitle = rawTitle.match(/미팅\s*(?:제안|수락|거절|취소)\s*:\s*(.+)$/)?.[1]?.trim();
      const actorFromMessage = rawMessage.match(/^(.+?)님이/)?.[1]?.trim();
      const name = actorFromTitle || actorFromMessage || '';
      const reason = rawMessage.match(/사유\s*:\s*(.+)$/)?.[1]?.trim() || rawMessage.match(/:\s*(.+)$/)?.[1]?.trim() || '';
      const time = rawMessage.match(/님이\s+(.+?)\s+미팅을/)?.[1]?.trim() || '';
      const location = rawMessage.match(/\(([^)]+)\)/)?.[1]?.trim() || '';
      const count = rawMessage.match(/후보\s*(\d+)개/)?.[1] || '';

      if (/미팅\s*시간을\s*제안|미팅\s*제안/.test(rawText)) {
        return { eventKey: 'meeting_proposed', name, title: defaultMeetingTitle, count };
      }
      if (/수락/.test(rawText)) {
        return { eventKey: 'meeting_accepted', name, title: defaultMeetingTitle, time, location };
      }
      if (/거절|거부/.test(rawText)) {
        return { eventKey: 'meeting_rejected', name, title: defaultMeetingTitle, time, location, reason };
      }
      if (/취소/.test(rawText)) {
        return { eventKey: 'meeting_cancelled', name, title: defaultMeetingTitle, time, location, reason };
      }
      if (/일정\s*변경|수정\s*제안|reschedule/i.test(rawText)) {
        return { eventKey: 'meeting_rescheduled', name, title: defaultMeetingTitle, time, location, reason };
      }

      return {};
    };

    const inferLegacySurveyTitle = () => {
      if (!SURVEY_NOTIFICATION_TYPES.includes(noti.notification_type)) return '';

      const rawSurveyTitle = asText(data.survey_title || data.survey_name || data.title || rawTitle);
      const dskMatch = rawSurveyTitle.match(/DSK\s*2026/i);
      if (dskMatch) return dskMatch[0].replace(/\s+/, ' ');

      return rawSurveyTitle
        .replace(/^[\s📋📝📢🔔]+/u, '')
        .replace(/에\s*참여해\s*주세요\.?$/u, '')
        .replace(/에\s*참여해주세요\.?$/u, '')
        .replace(/참여해\s*주세요\.?$/u, '')
        .replace(/참여해주세요\.?$/u, '')
        .trim();
    };

    const legacyMeeting = inferLegacyMeetingContext();
    const legacySurveyTitle = inferLegacySurveyTitle();
    const eventKey = data.event_key || data.eventKey || noti.event_key || legacyMeeting.eventKey || noti.notification_type;
    const actorName = data.actor_name || data.acceptor_name || data.decliner_name || data.proposer_name || legacyMeeting.name || '';
    const meetingTime = data.meeting_datetime || data.scheduled_start || data.proposed_datetime;
    const params = {
      name: actorName,
      title: data.meeting_title || legacyMeeting.title || legacySurveyTitle || data.survey_title || data.survey_name || data.title || data.event_title || rawTitle || defaultMeetingTitle,
      time: data.meeting_datetime_str || legacyMeeting.time || formatAbsoluteDate(meetingTime),
      location: asText(data.location || data.location_details || data.room_name) || legacyMeeting.location || '',
      count: asText(data.candidate_count || data.count) || legacyMeeting.count || '',
      reason: asText(data.reason || data.decline_reason || data.reschedule_reason || data.decline_message) || legacyMeeting.reason || '',
      survey: legacySurveyTitle || data.survey_title || data.survey_name || '',
    };
    const titleKey = `notifications.${eventKey}.title`;
    const messageKey = `notifications.${eventKey}.message`;
    const localizedTitle = tr(titleKey, rawTitle || tr('notifications.default_title', 'Notification'), params);
    const localizedMessage = tr(messageKey, rawMessage || '', params);

    return {
      title: localizedTitle.trim(),
      message: localizedMessage.trim(),
    };
  };
  // =============================================================================
  // 🔥🔥🔥 [2025-12-31 수정] 알림 클릭 핸들러 - 설문 알림 특별 처리
  // =============================================================================
  const handleClick = async (noti) => {
    if (swipedId === noti.id) {
      setSwipedId(null); // 스와이프 복구
      return; 
    }
    if (!noti.is_read) await markAsRead(noti.id);
    
    // 🔥 설문 알림인 경우 특별 처리
    if (SURVEY_NOTIFICATION_TYPES.includes(noti.notification_type)) {
      const surveySlug = noti.data?.survey_slug || 
                         noti.data?.surveySlug ||
                         noti.metadata?.survey_slug ||
                         noti.metadata?.surveySlug ||
                         noti.related_id;
      
      if (surveySlug) {
        console.log('📋 [NotificationModal] 설문 알림 클릭 - 이동:', surveySlug);
        onClose();
        navigate(`/survey/${surveySlug}`);
        return;
      }
    }
    
    // 일반 알림은 기존 로직
    if (noti.action_url) {
      onClose();
      // 🔥 action_url이 /surveys/로 잘못 올 수 있으므로 보정
      const correctedUrl = noti.action_url.replace('/surveys/', '/survey/');
      navigate(correctedUrl);
    }
  };
  // 터치 이벤트 핸들러 - 스와이프 삭제
  const onTouchStart = (e, id) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const onTouchEnd = (id) => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    if (isLeftSwipe) {
      deleteNotification(id);
    }
    setTouchStart(null);
    setTouchEnd(null);
  };
  return (
    <div className="noti-modal-overlay" onClick={onClose}>
      <div className="noti-modal-content" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="noti-modal-header">
          <h3>{tr('notifications.title', 'Notifications')} ({notifications.filter(n => !n.is_read).length})</h3>
          <div className="noti-header-actions">
            <button className="read-all-btn" onClick={() => markAllAsRead()}>
              <Check size={14} /> {tr('notifications.mark_all_read', 'Mark all read')}
            </button>
            <button className="close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>
        {/* List */}
        <div className="noti-list-container">
          {notifications.length === 0 ? (
            <div className="empty-state">
              <Bell size={40} strokeWidth={1.5} />
              <p>{tr('notifications.empty', 'No new notifications.')}</p>
            </div>
          ) : (
            notifications.map((noti) => {
              // 아이콘 결정 로직
              const isUrgent = noti.notification_type === 'urgent' || noti.notification_type === 'event_reminder';
              const localizedNotification = getNotificationText(noti);
              
              return (
                <div 
                  key={noti.id} 
                  className={'noti-item-wrapper ' + (!noti.is_read ? 'unread' : '')}
                  onTouchStart={(e) => onTouchStart(e, noti.id)}
                  onTouchMove={onTouchMove}
                  onTouchEnd={() => onTouchEnd(noti.id)}
                  onClick={() => handleClick(noti)}
                >
                  {/* 스와이프 시 보이는 삭제 배경 */}
                  <div className="swipe-delete-bg">
                    <Trash2 size={20} />
                  </div>
                  {/* 실제 컨텐츠 */}
                  <div className="noti-item-content">
                    <div className={'noti-icon-box ' + (isUrgent ? 'urgent' : 'normal')}>
                      {isUrgent ? <AlertCircle size={18} /> : <Megaphone size={18} />}
                    </div>
                    
                    <div className="noti-text">
                      <p className="noti-title">{localizedNotification.title}</p>
                      <p className="noti-message">{localizedNotification.message}</p>
                      <span className="noti-time">
                        <Clock size={12} style={{ marginRight: 4 }}/>
                        {formatDate(noti.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        
        {notifications.length > 0 && (
          <div className="swipe-hint">
            {tr('notifications.swipe_delete_hint', 'Swipe left to delete')}
          </div>
        )}
      </div>
    </div>
  );
};
export default NotificationModal;
